/**
 * Email Schedule Utilities
 * Calculate when automated emails will be sent based on submission period and fund settings
 */

export interface EmailScheduleItem {
  type: 'initial' | 'reminder_1' | 'reminder_2' | 'reminder_3';
  reminderCount: number;
  label: string;
  description: string;
  sendDate: Date;
}

/**
 * Get the current period (the one we're currently in, which will be collected next)
 * This is different from getPreviousPeriod which returns the last completed period
 */
export function getCurrentPeriod(frequency: 'monthly' | 'quarterly' | 'annual', timezone: string = 'UTC'): string {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
  const year = now.getFullYear();
  const month = now.getMonth();

  switch (frequency) {
    case 'monthly': {
      return `${year}-${String(month + 1).padStart(2, '0')}`;
    }

    case 'quarterly': {
      const currentQuarter = Math.floor(month / 3) + 1;
      return `Q${currentQuarter} ${year}`;
    }

    case 'annual': {
      return String(year);
    }

    default:
      return '';
  }
}

/**
 * Calculate the deadline for a submission period
 * Deadline = first day of month after period ends + reporting_day_of_month
 */
export function calculateDeadline(period: string, frequency: 'monthly' | 'quarterly' | 'annual', dayOfMonth: number): Date {
  if (frequency === 'quarterly') {
    const [quarter, year] = period.split(' ');
    if (!quarter || !year) {
      return new Date();
    }

    const quarterNum = parseInt(quarter.replace('Q', ''));
    const monthAfterQuarter = quarterNum * 3 + 1; // Q1->4, Q2->7, Q3->10, Q4->1
    const deadlineYear = monthAfterQuarter > 12 ? parseInt(year) + 1 : parseInt(year);
    const deadlineMonth = monthAfterQuarter > 12 ? 1 : monthAfterQuarter;

    return new Date(deadlineYear, deadlineMonth - 1, dayOfMonth);
  }

  if (frequency === 'monthly') {
    const [year, month] = period.split('-');
    if (!year || !month) {
      return new Date();
    }

    const nextMonth = parseInt(month) === 12 ? 1 : parseInt(month) + 1;
    const nextYear = parseInt(month) === 12 ? parseInt(year) + 1 : parseInt(year);

    return new Date(nextYear, nextMonth - 1, dayOfMonth);
  }

  if (frequency === 'annual') {
    const year = parseInt(period);
    return new Date(year + 1, 0, dayOfMonth); // January of next year
  }

  return new Date();
}

/**
 * Calculate email schedule based on deadline
 * Returns array of email schedule items with calculated send dates
 */
export function calculateEmailSchedule(period: string, frequency: 'monthly' | 'quarterly' | 'annual', dayOfMonth: number): EmailScheduleItem[] {
  const deadline = calculateDeadline(period, frequency, dayOfMonth);

  return [
    {
      type: 'initial',
      reminderCount: 0,
      label: 'Initial Request',
      description: 'Sent 2 weeks before deadline',
      sendDate: new Date(deadline.getTime() - 14 * 24 * 60 * 60 * 1000),
    },
    {
      type: 'reminder_1',
      reminderCount: 1,
      label: 'First Reminder',
      description: 'Sent 1 week before deadline',
      sendDate: new Date(deadline.getTime() - 7 * 24 * 60 * 60 * 1000),
    },
    {
      type: 'reminder_2',
      reminderCount: 2,
      label: 'Second Reminder',
      description: 'Sent 2 days before deadline',
      sendDate: new Date(deadline.getTime() - 2 * 24 * 60 * 60 * 1000),
    },
    {
      type: 'reminder_3',
      reminderCount: 3,
      label: 'Final Reminder',
      description: 'Sent on deadline day',
      sendDate: deadline,
    },
  ];
}

/**
 * Get period date range
 */
export function getPeriodDateRange(period: string, frequency: 'monthly' | 'quarterly' | 'annual'): { start: Date; end: Date } {
  if (frequency === 'quarterly') {
    const [quarter, year] = period.split(' ');
    if (!quarter || !year) {
      return { start: new Date(), end: new Date() };
    }

    const quarterNum = parseInt(quarter.replace('Q', ''));
    const startMonth = (quarterNum - 1) * 3; // Q1->0, Q2->3, Q3->6, Q4->9
    const endMonth = startMonth + 2;

    const start = new Date(parseInt(year), startMonth, 1);
    const end = new Date(parseInt(year), endMonth + 1, 0); // Last day of end month

    return { start, end };
  }

  if (frequency === 'monthly') {
    const [year, month] = period.split('-');
    if (!year || !month) {
      return { start: new Date(), end: new Date() };
    }

    const start = new Date(parseInt(year), parseInt(month) - 1, 1);
    const end = new Date(parseInt(year), parseInt(month), 0); // Last day of month

    return { start, end };
  }

  if (frequency === 'annual') {
    const year = parseInt(period);
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);

    return { start, end };
  }

  return { start: new Date(), end: new Date() };
}

/**
 * Format date for display
 */
export function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Calculate days until a date
 */
export function daysUntil(date: Date): number {
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}
